import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StepAnnotator } from "@/components/StepAnnotator";
import { Plus, Trash2, Volume2, ImageIcon } from "lucide-react";

function emptyStep(order) {
  return {
    id: `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    order,
    title: `Step ${order}`,
    body: "",
    tools: [],
    safety: [],
    requires_signoff: false,
    expected_duration_sec: null,
    media: { photo_url: null, page_image: null, illustration_url: null, annotations: [], audio: {} },
    branch: null,
  };
}

export function StepsEditor({ sopId, versionId, steps, setSteps }) {
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState("");

  const update = (i, patch) => {
    const next = steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setSteps(next);
  };

  const updateMedia = (i, mediaPatch) => {
    const s = steps[i];
    update(i, { media: { ...(s.media || {}), ...mediaPatch } });
  };

  const add = () => {
    const next = [...steps, emptyStep(steps.length + 1)];
    setSteps(next);
    setActive(next.length - 1);
  };

  const remove = (i) => {
    const next = steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 }));
    setSteps(next);
    setActive(Math.max(0, i - 1));
  };

  const persist = async () => {
    if (!sopId || !versionId) return;
    setBusy("save");
    try {
      await api.put(`/v1/wi/${sopId}/versions/${versionId}/steps`, { steps });
    } finally {
      setBusy("");
    }
  };

  const genTts = async () => {
    if (!versionId || !steps[active]) return;
    setBusy("tts");
    try {
      const { data } = await api.post(`/v1/media/versions/${versionId}/steps/${steps[active].id}/tts`, {
        languages: ["en"],
      });
      updateMedia(active, { audio: data.audio });
    } finally {
      setBusy("");
    }
  };

  const illustrate = async () => {
    if (!versionId || !steps[active]) return;
    setBusy("ill");
    try {
      const { data } = await api.post(`/v1/media/versions/${versionId}/steps/${steps[active].id}/illustrate`, {});
      updateMedia(active, { illustration_url: data.illustration_url });
    } finally {
      setBusy("");
    }
  };

  const saveAnnotations = async (annotations) => {
    updateMedia(active, { annotations });
    if (!versionId || !steps[active]) return;
    await api.post(`/v1/media/versions/${versionId}/steps/${steps[active].id}/annotate`, {
      annotations,
      photo_url: steps[active]?.media?.photo_url,
    });
  };

  const step = steps[active];

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-4" data-testid="steps-editor">
      <div className="space-y-2">
        {steps.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActive(i)}
            className={`w-full text-left px-3 py-2 rounded-xl text-sm ${
              i === active ? "bg-white/[0.08] text-white border border-white/10" : "text-[#9AA4B2] hover:bg-white/[0.04]"
            }`}
          >
            {s.order}. {s.title}
          </button>
        ))}
        <Button type="button" className="btn-ghost-glass rounded-full w-full" onClick={add}>
          <Plus size={14} className="mr-1" /> Add step
        </Button>
        <Button type="button" className="btn-primary-glow rounded-full w-full" disabled={busy === "save"} onClick={persist}>
          Save steps
        </Button>
      </div>

      {step ? (
        <div className="space-y-4 glass rounded-3xl p-5">
          <div className="flex justify-between gap-2">
            <input
              value={step.title}
              onChange={(e) => update(active, { title: e.target.value })}
              className="flex-1 bg-transparent border-b border-white/10 text-white font-display text-xl focus:outline-none focus:border-cyan-400/50"
            />
            <button type="button" className="text-rose-300" onClick={() => remove(active)}>
              <Trash2 size={16} />
            </button>
          </div>
          <textarea
            value={step.body}
            onChange={(e) => update(active, { body: e.target.value })}
            rows={5}
            className="w-full rounded-2xl bg-white/[0.04] border border-white/10 p-3 text-sm text-[#cbd5e1]"
            placeholder="Instruction body"
          />
          <label className="flex items-center gap-2 text-sm text-[#9AA4B2]">
            <input
              type="checkbox"
              checked={!!step.requires_signoff}
              onChange={(e) => update(active, { requires_signoff: e.target.checked })}
            />
            Require e-signature on complete
          </label>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Photo URL</label>
            <input
              value={step.media?.photo_url || ""}
              onChange={(e) => updateMedia(active, { photo_url: e.target.value || null })}
              className="mt-1 w-full rounded-full bg-white/[0.04] border border-white/10 px-4 py-2 text-sm text-white"
              placeholder="https://… or data URI"
            />
          </div>
          <StepAnnotator
            photoUrl={step.media?.photo_url}
            annotations={step.media?.annotations || []}
            onChange={saveAnnotations}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" className="btn-ghost-glass rounded-full" disabled={!!busy} onClick={genTts}>
              <Volume2 size={14} className="mr-1" /> {busy === "tts" ? "Generating…" : "Generate TTS"}
            </Button>
            <Button type="button" className="btn-ghost-glass rounded-full" disabled={!!busy} onClick={illustrate}>
              <ImageIcon size={14} className="mr-1" /> {busy === "ill" ? "…" : "Draft illustration"}
            </Button>
          </div>
          {step.media?.illustration_url && (
            <img src={step.media.illustration_url} alt="" className="rounded-2xl border border-white/10 max-h-48" />
          )}
        </div>
      ) : (
        <div className="text-[#5B6472] text-sm p-6">No steps yet — add one or derive from content/flowchart on save.</div>
      )}
    </div>
  );
}
